import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useMemo } from "react";
import { useEditGroupTransaction } from "features/groups/hooks/useEditGroupTransaction";
import { useTranslation } from "shared/hooks/useTranslation";
import { EditTransactionState, GroupTransaction } from "@/types/GroupTransaction";
import { UserEntry } from "@/types/Group";
import { formatCurrencyAmount, languageToLocale } from "shared/money/balances";
import { toAmount } from "features/groups/lib/transactionSplits";
import AmountCurrencyRow from "shared/transactions/AmountCurrencyRow";
import CategorySelect from "shared/transactions/CategorySelect";
import SplitTypeSelect from "shared/transactions/SplitTypeSelect";
import SplitBetweenSection from "shared/transactions/SplitBetweenSection";
import FormActions from "shared/ui/FormActions";
import { FORM_FIELD_GAP } from "shared/ui/formConstants";
import { useOfflineDisabled } from "shared/hooks/useOfflineDisabled";
import { participantDisplayName } from "features/groups/lib/groupPeople";

type ParticipantOption = Pick<UserEntry, "id" | "displayName">;

function dedupeParticipantOptions(people: ParticipantOption[] = []): ParticipantOption[] {
    const byId = new Map<string, ParticipantOption>();
    for (const person of people) {
        const id = String(person.id ?? "").trim();
        if (!id || byId.has(id)) continue;
        byId.set(id, {
            id,
            displayName: participantDisplayName({ ...person, id }),
        });
    }
    return [...byId.values()];
}

function EditGroupInnerTransactionForm({
    editTransaction,
    disabled,
    people,
    setField,
    handleSplitChange,
    onTogglePerson,
}: {
    editTransaction: EditTransactionState;
    disabled?: boolean;
    people: ParticipantOption[];
    setField: (key: keyof EditTransactionState, value: EditTransactionState[typeof key]) => void;
    handleSplitChange: (person: string, value: string | number) => void;
    onTogglePerson: (checked: boolean, person: string) => void;
}) {
    const { t } = useTranslation();

    const splitPeople = people.map((person) => {
        const included = (editTransaction.splitBetween ?? []).includes(person.id);
        const value = editTransaction.splitType === "equal" && included
            ? (editTransaction.customSplits?.[person.id] ?? "0.00").toString()
            : (editTransaction.customSplits?.[person.id] ?? "0.00").toString();
        return {
            id: person.id,
            label: participantDisplayName(person),
            included,
            value,
            readOnly: editTransaction.splitType === "equal",
        };
    });

    return (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={FORM_FIELD_GAP} sx={{ width: "100%" }}>
            <Stack spacing={FORM_FIELD_GAP} sx={{ flex: 1, maxWidth: 320 }}>
                <Typography variant="subtitle2">
                    {t("common.edit")} {editTransaction?.name ?? ""}
                </Typography>

                <TextField
                    fullWidth
                    size="small"
                    placeholder={t("forms.expenseName")}
                    value={editTransaction.name}
                    onChange={(e) => setField("name", e.target.value)}
                    disabled={disabled}
                />

                <AmountCurrencyRow
                    amount={editTransaction.amount}
                    currency={editTransaction.currency}
                    onAmountChange={(v) => setField("amount", v)}
                    onCurrencyChange={(v) => setField("currency", v)}
                    disabled={disabled}
                />

                <FormControl fullWidth size="small">
                    <InputLabel>{t("forms.payer")}</InputLabel>
                    <Select value={editTransaction.payer} label={t("forms.payer")} onChange={(e) => setField("payer", e.target.value)} disabled={disabled}>
                        {people.map((p) => (<MenuItem key={p.id} value={p.id}>{p.displayName}</MenuItem>))}
                    </Select>
                </FormControl>

                <CategorySelect
                    value={editTransaction.category ?? ""}
                    onChange={(v) => setField("category", v)}
                    disabled={disabled}
                />

                <SplitTypeSelect
                    value={editTransaction.splitType}
                    onChange={(v) => setField("splitType", v)}
                    disabled={disabled}
                />
            </Stack>

            <Box sx={{ flex: 1, maxWidth: 320 }}>
                <SplitBetweenSection
                    people={splitPeople}
                    onToggle={(personId, checked) => onTogglePerson(checked, personId)}
                    onValueChange={(personId, v) => handleSplitChange(personId, v)}
                    disabled={disabled}
                />
            </Box>
        </Stack>
    );
}

export default function EditGroupTransactionForm({
    transaction,
    people = [],
    onSave,
    onCancel,
    onDelete,
    disabled,
}: {
    transaction: EditTransactionState;
    people?: ParticipantOption[];
    onSave?: (tx: GroupTransaction) => void;
    onCancel?: () => void;
    onDelete?: (tx: GroupTransaction) => void;
    disabled?: boolean;
}) {
    const { t, currentLanguage } = useTranslation();
    const { isOnline } = useOfflineDisabled();
    const participantOptions = useMemo(() => dedupeParticipantOptions(people), [people]);
    const peopleIds = useMemo(() => participantOptions.map((p) => p.id), [participantOptions]);
    const { editTransaction, validationError, setField, handleSplitChange, onTogglePerson, buildUpdatedTransaction } = useEditGroupTransaction(transaction, peopleIds);

    const customSplitDeltaHint = useMemo(() => {
        if (editTransaction.splitType !== "custom") return null;
        const total = toAmount(editTransaction.amount);
        const splitSum = Object.values(editTransaction.customSplits ?? {}).reduce<number>(
            (acc, value) => acc + toAmount(value),
            0
        );
        const delta = Number((splitSum - total).toFixed(2));
        const absDelta = Math.abs(delta);
        if (absDelta < 0.01) {
            return { color: "success.main", text: t("forms.customSplitMatched") };
        }
        const formattedAmount = formatCurrencyAmount(
            absDelta,
            editTransaction.currency || "ARS",
            languageToLocale(currentLanguage)
        );
        if (delta > 0) {
            return {
                color: "error.main",
                text: t("forms.customSplitExceeded", { amount: formattedAmount }),
            };
        }
        return {
            color: "warning.main",
            text: t("forms.customSplitRemaining", { amount: formattedAmount }),
        };
    }, [editTransaction.splitType, editTransaction.amount, editTransaction.customSplits, editTransaction.currency, currentLanguage, t]);

    const handleSave = () => {
        if (!editTransaction.amount || !editTransaction.payer || validationError) return;
        onSave?.(buildUpdatedTransaction() as GroupTransaction);
    };

    const submitDisabled = !!validationError || !editTransaction.amount || !editTransaction.payer;
    const offlineLocked = !isOnline;
    const formDisabled = Boolean(disabled) || offlineLocked;

    return (
        <Box>
            <EditGroupInnerTransactionForm
                editTransaction={editTransaction}
                disabled={formDisabled}
                people={participantOptions}
                setField={setField}
                handleSplitChange={handleSplitChange}
                onTogglePerson={onTogglePerson}
            />

            {customSplitDeltaHint ? (
                <Typography variant="body2" sx={{ mt: FORM_FIELD_GAP, color: customSplitDeltaHint.color }}>
                    {customSplitDeltaHint.text}
                </Typography>
            ) : null}

            {validationError ? (
                <Typography variant="body2" color="error" sx={{ mt: FORM_FIELD_GAP }}>{t(validationError)}</Typography>
            ) : null}

            <FormActions
                submitLabel={t("common.save")}
                submitButtonType="button"
                onSubmit={handleSave}
                onCancel={() => onCancel?.()}
                submitDisabled={submitDisabled || offlineLocked}
                submitLoading={Boolean(disabled)}
                cancelDisabled={disabled}
                destructiveAction={onDelete
                    ? {
                        label: t("common.delete"),
                        onClick: () => onDelete(buildUpdatedTransaction() as GroupTransaction),
                        disabled: Boolean(disabled) || offlineLocked,
                    }
                    : undefined}
            />
        </Box>
    );
}
